import { getFromCache, putInCache, hashScript } from "./geometryCache";
import type { ParamMeta } from "../workers/cadWorker";

export type { ParamMeta };

export interface MeshResult {
  vertices: Float32Array;
  indices: Uint32Array;
  normals: Float32Array;
}

export type FeatureMeshMap = Record<string, MeshResult>;

export type ScriptRunnerStatus = "idle" | "loading" | "computing" | "error";

type StatusCallback = (status: ScriptRunnerStatus, message?: string) => void;
type GeometryCallback = (mesh: MeshResult | null, featureMeshes?: FeatureMeshMap) => void;
type ParamsCallback = (params: ParamMeta[]) => void;

/** Combine multiple per-feature meshes into a single MeshResult */
function combineMeshes(map: FeatureMeshMap): MeshResult {
  const entries = Object.values(map);
  if (entries.length === 1) return entries[0];

  let totalVerts = 0, totalIdx = 0, totalNorms = 0;
  for (const m of entries) {
    totalVerts += m.vertices.length;
    totalIdx += m.indices.length;
    totalNorms += m.normals.length;
  }

  const vertices = new Float32Array(totalVerts);
  const indices = new Uint32Array(totalIdx);
  const normals = new Float32Array(totalNorms);

  let vOff = 0, iOff = 0, nOff = 0, vertexOffset = 0;
  for (const m of entries) {
    vertices.set(m.vertices, vOff);
    normals.set(m.normals, nOff);
    for (let j = 0; j < m.indices.length; j++) {
      indices[iOff + j] = m.indices[j] + vertexOffset;
    }
    vOff += m.vertices.length;
    iOff += m.indices.length;
    nOff += m.normals.length;
    vertexOffset += m.vertices.length / 3;
  }

  return { vertices, indices, normals };
}

export class ScriptRunner {
  private worker: Worker | null = null;
  private readyPromise: Promise<void>;
  private resolveReady!: () => void;
  private pendingResolve: ((mesh: MeshResult) => void) | null = null;
  private pendingReject: ((err: Error) => void) | null = null;
  private pendingExportResolve: ((blob: Blob) => void) | null = null;
  private pendingExportReject: ((err: Error) => void) | null = null;
  private pendingDrawingResolve: ((result: { visiblePaths: string; hiddenPaths: string }) => void) | null = null;
  private pendingDrawingReject: ((err: Error) => void) | null = null;
  private pendingInterferenceResolve: ((result: { interferes: boolean; vertexCount: number }) => void) | null = null;
  private onStatus: StatusCallback;
  private onGeometry: GeometryCallback;
  private onParams: ParamsCallback;
  private lastParams: ParamMeta[] = [];

  constructor(
    onStatus: StatusCallback,
    onGeometry: GeometryCallback,
    onParams: ParamsCallback = () => {}
  ) {
    this.onStatus = onStatus;
    this.onGeometry = onGeometry;
    this.onParams = onParams;
    this.readyPromise = new Promise((res) => {
      this.resolveReady = res;
    });
    this.onStatus("loading");
    this.initWorker();
  }

  private initWorker() {
    this.worker = new Worker(
      new URL("../workers/cadWorker.ts", import.meta.url),
      { type: "module" }
    );

    this.worker.onmessage = (e: MessageEvent) => {
      const { type, mesh, message, params } = e.data;

      switch (type) {
        case "READY":
          this.resolveReady();
          this.onStatus("idle");
          break;

        case "GEOMETRY":
          if (mesh && this.pendingResolve) {
            this.pendingResolve(mesh);
            this.pendingResolve = null;
            this.pendingReject = null;
          }
          this.onGeometry(mesh);
          if (params) {
            this.lastParams = params;
            this.onParams(params);
          }
          this.onStatus("idle");
          break;

        case "FEATURE_GEOMETRY": {
          const fm = e.data.featureMeshes as FeatureMeshMap;
          const combined = combineMeshes(fm);
          if (this.pendingResolve) {
            this.pendingResolve(combined);
            this.pendingResolve = null;
            this.pendingReject = null;
          }
          this.onGeometry(combined, fm);
          if (params) {
            this.lastParams = params;
            this.onParams(params);
          }
          this.onStatus("idle");
          break;
        }

        case "EXPORT_RESULT": {
          const { blob: buffer, format } = e.data;
          if (this.pendingExportResolve) {
            const mimeType = format === "step" ? "application/STEP" : "application/sla";
            const blob = new Blob([buffer], { type: mimeType });
            this.pendingExportResolve(blob);
            this.pendingExportResolve = null;
            this.pendingExportReject = null;
          }
          this.onStatus("idle");
          break;
        }

        case "DRAWING_VIEW":
          if (this.pendingDrawingResolve) {
            this.pendingDrawingResolve({
              visiblePaths: e.data.visiblePaths,
              hiddenPaths: e.data.hiddenPaths,
            });
            this.pendingDrawingResolve = null;
            this.pendingDrawingReject = null;
          }
          this.onStatus("idle");
          break;

        case "INTERFERENCE_RESULT":
          if (this.pendingInterferenceResolve) {
            this.pendingInterferenceResolve({ interferes: e.data.interferes, vertexCount: e.data.vertexCount ?? 0 });
            this.pendingInterferenceResolve = null;
          }
          break;

        case "ERROR":
          if (this.pendingDrawingReject) {
            this.pendingDrawingReject(new Error(message));
            this.pendingDrawingResolve = null;
            this.pendingDrawingReject = null;
          } else if (this.pendingExportReject) {
            this.pendingExportReject(new Error(message));
            this.pendingExportResolve = null;
            this.pendingExportReject = null;
          } else if (this.pendingReject) {
            this.pendingReject(new Error(message));
            this.pendingResolve = null;
            this.pendingReject = null;
          }
          this.onStatus("error", message);
          break;
      }
    };

    this.worker.onerror = (err) => {
      console.error("Worker error:", err);
      this.onStatus("error", err.message);
    };
  }

  async execute(
    script: string,
    paramValues?: Record<string, number>
  ): Promise<MeshResult> {
    await this.readyPromise;

    const cacheInput = paramValues
      ? script + "\n__params__:" + JSON.stringify(paramValues)
      : script;
    const hash = await hashScript(cacheInput);
    const cached = await getFromCache(hash);
    if (cached) {
      this.onGeometry(cached.mesh);
      if (cached.params) {
        this.lastParams = cached.params;
        this.onParams(cached.params);
      }
      this.onStatus("idle");
      return cached.mesh;
    }

    if (this.pendingReject) {
      this.pendingReject(new Error("Superseded by newer execution"));
      this.pendingResolve = null;
      this.pendingReject = null;
    }

    this.onStatus("computing");

    return new Promise<MeshResult>((resolve, reject) => {
      this.pendingResolve = (mesh) => {
        putInCache(hash, mesh, this.lastParams).catch(() => {});
        resolve(mesh);
      };
      this.pendingReject = reject;

      this.worker!.postMessage({
        type: "EXECUTE",
        script,
        id: hash,
        paramValues,
      });
    });
  }

  async checkInterference(scriptA: string, scriptB: string): Promise<{ interferes: boolean; vertexCount: number }> {
    await this.readyPromise;
    const id = `interference-${Date.now()}`;
    return new Promise((resolve) => {
      this.pendingInterferenceResolve = resolve;
      this.worker!.postMessage({ type: "INTERFERENCE_CHECK", scriptA, scriptB, id });
      // Timeout after 10s — assume no interference
      setTimeout(() => {
        if (this.pendingInterferenceResolve) {
          this.pendingInterferenceResolve({ interferes: false, vertexCount: 0 });
          this.pendingInterferenceResolve = null;
        }
      }, 10000);
    });
  }

  async importStep(file: File): Promise<MeshResult> {
    await this.readyPromise;

    if (this.pendingReject) {
      this.pendingReject(new Error("Superseded by STEP import"));
      this.pendingResolve = null;
      this.pendingReject = null;
    }

    this.onStatus("computing");

    const buffer = await file.arrayBuffer();
    const id = `step-${Date.now()}`;

    return new Promise<MeshResult>((resolve, reject) => {
      this.pendingResolve = (mesh) => {
        resolve(mesh);
      };
      this.pendingReject = reject;

      this.worker!.postMessage(
        { type: "IMPORT_STEP", buffer, id },
        [buffer]
      );
    });
  }

  async exportShape(format: "stl" | "step"): Promise<Blob> {
    await this.readyPromise;

    this.onStatus("computing");
    const id = `export-${format}-${Date.now()}`;

    return new Promise<Blob>((resolve, reject) => {
      this.pendingExportResolve = resolve;
      this.pendingExportReject = reject;

      this.worker!.postMessage({ type: "EXPORT", format, id });
    });
  }

  async generateDrawingView(
    direction: [number, number, number]
  ): Promise<{ visiblePaths: string; hiddenPaths: string }> {
    await this.readyPromise;

    this.onStatus("computing");
    const id = `drawing-${Date.now()}`;

    return new Promise((resolve, reject) => {
      this.pendingDrawingResolve = resolve;
      this.pendingDrawingReject = reject;

      this.worker!.postMessage({ type: "GENERATE_DRAWING_VIEW", direction, id });
    });
  }

  dispose() {
    this.worker?.terminate();
    this.worker = null;
  }
}
